<template>
  <b-modal
    v-model="showModal"
    size="lg"
    id="service-categories-modal"
    :title="edited.id > 0 ? 'Edit service category details' : 'Add new service category'"
    :ok-title="isFormLoading ? 'Saving...' : 'Save'"
    :ok-disabled="isFormLoading"
    cancel-variant="outline-secondary"
    @ok="onSubmit($event)"
  >
    <div
      v-if="isContentLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>
    <b-form v-else>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Name"
            label-for="name"
          >
            <b-form-input
              v-model="edited.name"
              placeholder="Name"
            />
            <small
              v-if="v$.edited?.name.$error"
              class="text-danger"
            >Name is required</small>
          </b-form-group>
          <b-form-group
            label="Description"
            label-for="description"
          >
            <b-form-textarea
              v-model="edited.description"
              placeholder="Description"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
          <b-form-group
            label="Parent category"
            label-for="parentCategoryId"
          >
            <b-form-select
              id="parentCategoryId"
              v-model="edited.parent_category_id"
            >
              <b-form-select-option :value="null">
                None
              </b-form-select-option>
              <template
                v-for="category in serviceCategories"
                :key="category.id"
              >
                <b-form-select-option
                  v-if="category.id !== edited.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
          <b-form-group
            label="Picture"
            label-for="picture"
          >
            <b-form-file
              v-model="edited.picture"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
            />
            <img
              v-if="edited.picture_preview"
              :src="edited.picture_preview.includes('https') ? edited.picture_preview : '//' + edited.picture_preview"
              :style="{ width: '150px', border: '1px solid #DDDDDD' }"
              class="my-1"
              alt=""
            >
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script setup>
import { watch, ref, reactive } from 'vue'
import { useStore } from 'vuex'
import {
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import useVuelidate from '@vuelidate/core'
import { minLength, required } from '@vuelidate/validators'
import { toastFailure, toastSuccess } from '@/libs/toastification'

const cloneObject = (object) => JSON.parse(JSON.stringify(object))

const defaultServiceCategory = {
  id: null,
  name: null,
  description: null,
  picture: null,
  picture_preview: null,
  parent_category_id: null,
}

const props = defineProps({
  serviceCategories: {
    type: Array,
    required: true,
  },
  serviceCategory: {
    type: Object,
    default: null,
  },
  clickDate: {
    type: Date,
    default: null,
  },
})

const store = useStore()
const showModal = ref(false)
const isContentLoading = ref(true)
const isFormLoading = ref(false)
let temporaryPicture = null
const edited = reactive(cloneObject(defaultServiceCategory))

const rules = {
  edited: {
    name: { required, minLength: minLength(2) },
  },
}
const v$ = useVuelidate(rules, { edited })

const serviceCategoryState = () => store.state.serviceCategories.serviceCategory

watch(
  () => store.state.serviceCategories.serviceCategory,
  (newCategory) => {
    if (newCategory?.data && newCategory.data.id > 0) {
      edited.id = newCategory.data.id
      edited.name = newCategory.data.name
      edited.description = newCategory.data.description
      edited.picture_preview = newCategory.data.picture
      edited.parent_category_id = newCategory.data.parent_category_id
    } else {
      Object.assign(edited, cloneObject(defaultServiceCategory))
    }
  },
)

watch(
  () => props.clickDate,
  () => {
    if (!props.clickDate) {
      return
    }

    isContentLoading.value = true

    if (props.serviceCategory !== null) {
      store.dispatch('serviceCategories/loadServiceCategory', props.serviceCategory.id)
        .then(() => {
          isContentLoading.value = false
          showModal.value = true
        })
        .catch((error) => {
          isContentLoading.value = false
          showModal.value = false
          toastFailure(`Something went wrong: ${error.message}`)
        })
    } else {
      store.commit('serviceCategories/SET_SERVICE_CATEGORY', null)
      Object.assign(edited, cloneObject(defaultServiceCategory))
      isContentLoading.value = false
      showModal.value = true
    }
  },
)

const onSubmit = async (event) => {
  if (isFormLoading.value) {
    return
  }

  event.preventDefault()
  isFormLoading.value = true
  const isFormCorrect = await v$.value.edited.$validate()
  if (!isFormCorrect) {
    isFormLoading.value = false
    return
  }

  await submitForm()
}

const getTemporaryPicture = async () => new Promise((resolve) => {
  const reader = new FileReader()
  reader.addEventListener('load', () => {
    temporaryPicture = reader.result
    resolve()
  }, false)

  if (edited.picture && /\.(jpe?g|png|gif)$/i.test(edited.picture.name)) {
    reader.readAsDataURL(edited.picture)
  } else {
    resolve()
  }
})

const submitForm = async () => {
  const formData = new FormData()

  for (const [key, value] of Object.entries(edited)) {
    if (value !== null && value !== undefined && value !== '') {
      formData.append(key, value)
    }
  }

  if (edited.picture) {
    await getTemporaryPicture()
  }

  const action = edited.id != null
    ? 'serviceCategories/updateServiceCategory'
    : 'serviceCategories/addServiceCategory'

  if (edited.id != null) {
    formData.append('_method', 'PUT')
  }

  store.dispatch(action, { formData })
    .then(() => {
      showModal.value = false
      toastSuccess(edited.id != null ? 'Successfully updated' : 'Successfully added')
    })
    .catch(() => {
      toastFailure(edited.id != null ? 'Failed to update' : 'Failed to add')
    })
    .finally(() => {
      isFormLoading.value = false
      edited.picture = null
      temporaryPicture = null
    })
}
</script>
